import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useT } from '@transifex/react';

import { getEmailFromAccessToken } from '../biometric/accessTokenClaims';
import { useBiometricEnrollmentPrompt } from '../biometric/BiometricEnrollmentProvider';
import { AuthFooter } from '../components/AuthFooter';
import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import Loading from '../components/Loading';
import { Logo } from '../components/Logo';
import ThemedText from '../components/ThemedText';
import { ROUTES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { analytics } from '../utils/analytics';
import { completeAuth, handleTokenLoginExchange } from '../services/authService';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnAuthenticated } from '../hooks/useOnAuthenticated';

const tid = screenTestId('TokenLogin');

/**
 * Magic-link callback. Consumes ?login_token=<sesame> from the email URL,
 * exchanges it via Apigee for an Apigee-minted JWT, and redirects to the
 * resolved app destination.
 *
 * Mirrors sso-callback.tsx — same "consume opaque code, exchange, redirect"
 * shape, just for a different exchange endpoint. On failure, shows an error
 * with a link back to /magic-link so the user can request a fresh link.
 */
export default function TokenLoginScreen() {
  const router = useLoginRouter();
  const params = useLocalSearchParams();
  const t = useT();
  const onAuthenticated = useOnAuthenticated();
  const { promptIfEligible } = useBiometricEnrollmentPrompt();
  const [error, setError] = useState<string | null>(null);

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const returnTo = typeof params.return_to === 'string' ? params.return_to : undefined;

  useEffect(() => {
    const loginToken = typeof params.login_token === 'string' ? params.login_token : undefined;
    let cancelled = false;

    (async () => {
      if (!loginToken) {
        analytics.trackEvent('magic_link_exchange_missing_token');
        router.replace(ROUTES.MAGIC_LINK);
        return;
      }

      try {
        analytics.trackEvent('magic_link_exchange_started');
        const { token, nextApp } = await handleTokenLoginExchange(loginToken);
        if (cancelled) return;

        analytics.trackEvent('magic_link_exchange_succeeded');
        await promptIfEligible({
          accessToken: token,
          email: getEmailFromAccessToken(token) ?? '',
        });
        await completeAuth({
          token,
          method: 'token_login',
          urlNextUrl: nextUrl,
          urlReturnTo: returnTo,
          nextApp,
          onAuthenticated,
        });
      } catch {
        if (cancelled) return;
        analytics.trackEvent('magic_link_exchange_failed');
        setError(t('This sign in link is invalid or has expired. Please request a new one.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRequestNewLink = () => {
    router.replace(ROUTES.MAGIC_LINK);
  };

  const handleBackToLogin = () => {
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />
        {error ? (
          <View className='w-full' testID={tid('container')}>
            <InlineError message={error} testID={tid('error')} />
            <View className='mt-6'>
              <BloomButton
                text={t('Request a new link')}
                onPress={handleRequestNewLink}
                testID={tid('request-new-link-button')}
              />
            </View>
            <AuthFooter
              variant='secondary'
              onBackToLogin={handleBackToLogin}
              testID={tid('footer')}
            />
          </View>
        ) : (
          <View className='items-center justify-center py-16' testID={tid('container')}>
            <Loading className='flex-none' />
            <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
              {t('Signing you in...')}
            </ThemedText>
          </View>
        )}
      </View>
    </AuthLayout>
  );
}
